<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { CustomerCase, PatchedCustomerCaseRequest } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'
  import { CasesService } from '@/types/generated/services.gen'
  import type { GenericObject } from 'vee-validate'
  import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
  import { CompanyLegalFormEnum, TitleEnum } from '@/types/generated/types.gen'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import { dateRule } from '@/lib/util/dateRule'

  const props = defineProps<{
    caseData: CustomerCase
    debounceDelay?: number
    throttleInterval?: number
  }>()

  const emit = defineEmits(['update:case-data', 'update:complete'])

  const { t } = useTranslation({
    en: {
      personalInformation: 'Client',
      title: 'Title',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      street: 'Street',
      streetNumber: 'House Number',
      zip: 'ZIP Code',
      city: 'City',
      companyLegalForm: 'Legal Form',
      companyName: 'Company Name',
      contactTitle: 'Contact Title',
      contactFirstName: 'Contact First Name',
      contactLastName: 'Contact Last Name',
      isCompany: 'Is Legal Entity (juristische Person)',
      invalidEmail: 'Invalid email',
      emailRequired: 'Email is required',
      phoneRequired: 'Phone is required',
      firstNameRequired: 'First Name is required',
      lastNameRequired: 'Last Name is required',
      companyNameRequired: 'Company Name is required',
      legalFormRequired: 'Legal Form is required',
      dateOfBirth: 'Date of Birth',
      datePlaceholder: 'DD.MM.YYYY',
      titleValidOptions: 'Please select a valid option'
    },
    de: {
      personalInformation: 'Mandant',
      title: 'Anrede',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefon',
      address: 'Adresse',
      street: 'Straße',
      streetNumber: 'Hausnummer',
      zip: 'Postleitzahl',
      city: 'Stadt',
      companyLegalForm: 'Rechtsform',
      companyName: 'Firma',
      contactTitle: 'Kontakt Anrede',
      contactFirstName: 'Kontakt Vorname',
      contactLastName: 'Kontakt Nachname',
      isCompany: 'Ist juristische Person',
      invalidEmail: 'Ungültige E-Mail-Adresse',
      emailRequired: 'E-Mail ist erforderlich',
      phoneRequired: 'Telefon ist erforderlich',
      firstNameRequired: 'Vorname ist erforderlich',
      lastNameRequired: 'Nachname ist erforderlich',
      companyNameRequired: 'Firmenname ist erforderlich',
      legalFormRequired: 'Rechtsform ist erforderlich',
      dateOfBirth: 'Geburtsdatum',
      datePlaceholder: 'TT.MM.JJJJ',
      titleValidOptions: 'Bitte wählen Sie eine gültige Option'
    }
  })

  const caseData = ref(props.caseData)
  const isCompany = ref(caseData.value.data.client.is_company)
  const activeTab = ref(isCompany.value ? 'juristische' : 'naturliche')

  watch(activeTab, (newValue) => {
    const newIsCompany = newValue === 'juristische'
    if (isCompany.value !== newIsCompany) {
      isCompany.value = newIsCompany
      // eslint-disable-next-line vue/no-mutating-props
      props.caseData.data.client.is_company = newIsCompany
      handleSave({ is_company: newIsCompany })
    }
  })

  const companyLegalFormOptions = computed(() =>
    Object.entries(CompanyLegalFormEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  const contactTitleOptions = computed(() =>
    Object.entries(TitleEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  const handleSave = async (updatedData: GenericObject) => {
    emit('update:case-data', 'client', updatedData)
  }

  const baseSchema = {
    email: yup.string().email(t('invalidEmail')).required(t('emailRequired'))
  }

  const individualSchema = {
    ...baseSchema,
    first_name: yup.string().required(t('firstNameRequired')),
    last_name: yup.string().required(t('lastNameRequired')),
    title: yup
      .string()
      .required(t('title'))
      .oneOf([TitleEnum.HERR, TitleEnum.FRAU, TitleEnum.DIVERS], t('titleValidOptions'))
  }

  const companySchema = {
    ...baseSchema,
    company_name: yup.string().required(t('companyNameRequired')),
    company_legal_form: yup.string().required(t('legalFormRequired'))
  }

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    updateSchema,
    showErrors
  } = useFormWithValidation(
    props.caseData.data.client,
    isCompany.value ? companySchema : individualSchema
  )

  defineExpose({
    validate,
    showErrors
  })

  watch(isCompany, (newValue) => {
    handleSave({ is_company: newValue })
  })

  watch(isCompany, (newValue) => {
    updateSchema(newValue ? companySchema : individualSchema)
  })

  const emailValidation = yup
    .string()
    .email('Invalid email format')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
    .test('email-tld', 'Invalid email domain', (value) => {
      if (!value) return false // Fail if empty (handled by .required())
      const parts = value.split('@')
      return parts.length === 2 && parts[1].includes('.') && parts[1].split('.').pop()!.length >= 2
    })
    .required('Email is required')
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-xl font-semibold text-gray-900">{{ t('personalInformation') }}</h2>
    <Tabs
      v-model="activeTab"
      class="w-full"
    >
      <TabsList class="mb-4 grid w-full grid-cols-2">
        <TabsTrigger value="naturliche">Natürliche Person</TabsTrigger>
        <TabsTrigger value="juristische">Juristische Person</TabsTrigger>
      </TabsList>
      <TabsContent value="naturliche">
        <AutoSavingForm
          :debounce-delay="debounceDelay"
          :throttle-interval="throttleInterval"
          class="space-y-8"
          @save="handleSave"
        >
          <div class="grid-cols-0 grid gap-x-2 gap-y-1 sm:grid-cols-6">
            <div class="sm:col-span-2">
              <SelectField
                v-model="caseData.data.client.title"
                :options="contactTitleOptions"
                :label="t('title')"
                name="title"
                :is-filterable="true"
                :error="formErrors.value?.title"
                :rules="individualSchema.title"
                @update:model-value="(value: any) => handleFieldChange('title', value)"
                @focus="() => handleFieldTouch('title')"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.first_name"
                :label="t('firstName')"
                name="first_name"
                :rules="yup.string().required()"
                :error="formErrors?.value?.first_name ?? null"
                @update:model-value="(value: any) => handleFieldChange('first_name', value)"
                @focus="() => handleFieldTouch('first_name')"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.last_name"
                :label="t('lastName')"
                name="last_name"
                :rules="yup.string().required()"
                :error="formErrors?.value?.last_name ?? null"
                @update:model-value="(value: any) => handleFieldChange('last_name', value)"
                @focus="() => handleFieldTouch('last_name')"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.email"
                :label="t('email')"
                name="email"
                type="email"
                :rules="emailValidation"
                :error="formErrors?.value?.email ?? null"
                @update:model-value="(value: any) => handleFieldChange('email', value)"
                @focus="() => handleFieldTouch('email')"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.phone"
                :label="t('phone')"
                name="phone"
                type="tel"
                :rules="yup.string().optional()"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.date_of_birth"
                :label="t('dateOfBirth')"
                name="date_of_birth"
                :placeholder="t('datePlaceholder')"
                :rules="dateRule"
                @update:model-value="(value: any) => handleFieldChange('date_of_birth', value)"
                @focus="() => handleFieldTouch('date_of_birth')"
              />
            </div>
            <div class="sm:col-span-4">
              <InputField
                v-model="caseData.data.client.street"
                :label="t('street')"
                name="street"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.street_number"
                :label="t('streetNumber')"
                name="street_number"
              />
            </div>
            <div class="sm:col-span-4">
              <InputField
                v-model="caseData.data.client.city"
                :label="t('city')"
                name="city"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.zip"
                :label="t('zip')"
                name="zip"
              />
            </div>
          </div>
        </AutoSavingForm>
      </TabsContent>
      <TabsContent value="juristische">
        <AutoSavingForm
          :debounce-delay="debounceDelay"
          :throttle-interval="throttleInterval"
          class="space-y-8"
          @save="handleSave"
        >
          <div class="grid-cols-0 grid gap-x-2 gap-y-1 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <SelectField
                v-model="caseData.data.client.company_legal_form"
                :options="companyLegalFormOptions"
                :label="t('companyLegalForm')"
                name="company_legal_form"
                :is-filterable="true"
                :rules="yup.string().required()"
                :error="formErrors?.value?.company_legal_form ?? null"
                @update:model-value="(value: any) => handleFieldChange('company_legal_form', value)"
                @focus="() => handleFieldTouch('company_legal_form')"
              />
            </div>
            <div class="sm:col-span-3">
              <InputField
                v-model="caseData.data.client.company_name"
                :label="t('companyName')"
                name="company_name"
                :rules="yup.string().required()"
                :error="formErrors?.value?.company_name ?? null"
                @update:model-value="(value: any) => handleFieldChange('company_name', value)"
                @focus="() => handleFieldTouch('company_name')"
              />
            </div>
            <div class="sm:col-span-2">
              <SelectField
                v-model="caseData.data.client.title"
                :options="contactTitleOptions"
                :label="t('contactTitle')"
                name="title"
                :is-filterable="false"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.first_name"
                :label="t('contactFirstName')"
                name="first_name"
                @update:model-value="(value: any) => handleFieldChange('first_name', value)"
                @focus="() => handleFieldTouch('first_name')"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.last_name"
                :label="t('contactLastName')"
                name="last_name"
                @update:model-value="(value: any) => handleFieldChange('last_name', value)"
                @focus="() => handleFieldTouch('last_name')"
              />
            </div>
            <div class="sm:col-span-3">
              <InputField
                v-model="caseData.data.client.email"
                :label="t('email')"
                name="email"
                type="email"
                :rules="emailValidation"
                :error="formErrors?.value?.email ?? null"
                @update:model-value="(value: any) => handleFieldChange('email', value)"
                @focus="() => handleFieldTouch('email')"
              />
            </div>
            <div class="sm:col-span-3">
              <InputField
                v-model="caseData.data.client.phone"
                :label="t('phone')"
                name="phone"
                type="tel"
                :rules="yup.string().required()"
              />
            </div>
            <div class="sm:col-span-4">
              <InputField
                v-model="caseData.data.client.street"
                :label="t('street')"
                name="street"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.street_number"
                :label="t('streetNumber')"
                name="streetNumber"
              />
            </div>
            <div class="sm:col-span-4">
              <InputField
                v-model="caseData.data.client.city"
                :label="t('city')"
                name="city"
              />
            </div>
            <div class="sm:col-span-2">
              <InputField
                v-model="caseData.data.client.zip"
                :label="t('zip')"
                name="zip"
              />
            </div>
          </div>
        </AutoSavingForm>
      </TabsContent>
    </Tabs>
  </div>
</template>
